import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styles from './chat-message.scss';
import { MESSAGE_TYPES } from '../../containers/chat-agent/constants';
import { Copy, Check, User, Bot, ThumbsUp, ThumbsDown, X, RefreshCw, ZoomIn } from 'lucide-react';
import SaveToCsvButton from './components/SaveToCsvButton/SaveToCsvButton';
import { parseChartConfig, extractTablesFromContent } from './utils/chatContentParsing';
import {
  exportMarkdownTableToCsv,
  exportHtmlTableToCsv,
  exportParsedTableToCsv,
  exportChartToCsv,
  chartToCsv,
} from './utils/csvExport';

// Image Modal Component for expanding table images
const ImageModal = ({ src, alt, isOpen, onClose, isLightMode }) => {
  if (!isOpen) return null;
  
  return (
    <div 
      className={`${styles.imageModal} ${isLightMode ? styles.lightMode : ''}`}
      onClick={onClose}
    >
      <div className={styles.imageModalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.imageModalClose} onClick={onClose}>
          <X size={24} />
        </button>
        <img src={src} alt={alt} className={styles.imageModalImage} />
        {alt && alt !== 'Product' && (
          <p className={styles.imageModalCaption}>{alt}</p>
        )}
      </div>
    </div>
  );
};

const CITE_TOKEN = 'CITETOKEN_';

const CitationMarker = ({ num, citation, isLightMode }) => {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [tooltipPos, setTooltipPos] = useState(null);
  const wrapperRef = useRef(null);
  const supRef = useRef(null);

  useEffect(() => {
    if (!pinned) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setPinned(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [pinned]);

  const handleMouseEnter = () => {
    if (supRef.current) {
      const rect = supRef.current.getBoundingClientRect();
      setTooltipPos({
        top: rect.top - 100,   // offset up by fixed amount — avoids translateY flash
        left: rect.left + rect.width / 2,
      });
      requestAnimationFrame(() => setHovered(true));
    } else {
      setHovered(true);
    }
  };

  const visible = (hovered || pinned) && tooltipPos !== null;

  return (
    <span
      ref={wrapperRef}
      className={styles.citationWrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setHovered(false)}
    >
      <sup
        ref={supRef}
        className={`${styles.citationMarker} ${isLightMode ? styles.citationMarkerLight : ''} ${pinned ? styles.citationMarkerPinned : ''}`}
        onClick={(e) => { e.stopPropagation(); setPinned(p => !p); }}
      >
        [{num}]
      </sup>
      {visible && ReactDOM.createPortal(
        <span
          className={`${styles.citationTooltip} ${isLightMode ? styles.citationTooltipLight : ''}`}
          style={{
            position: 'fixed',
            top: tooltipPos.top,
            left: tooltipPos.left,
            transform: 'translateX(-50%)',
          }}
        >
          <strong className={styles.citationTooltipTitle}>{citation.source_name}</strong>
          {(citation.retailer || citation.keywords?.length > 0) && (
            <span className={styles.citationTooltipMeta}>
              {[citation.retailer, citation.keywords?.join(', ')].filter(Boolean).join(' · ')}
            </span>
          )}
          {citation.period && <span className={styles.citationTooltipPeriod}>{citation.period}</span>}
          {citation.description && <span className={styles.citationTooltipDesc}>{citation.description}</span>}
        </span>,
        document.body
      )}
    </span>
  );
};

const ChatMessage = React.memo(({
  message, 
  isLightMode, 
  onFeedback, 
  onRegenerate,
  isLoading,           // ✅ NEW: Is a request in progress?
  isLastAgentMessage   // ✅ NEW: Is this the last agent message?
}) => {
  const { type, content, timestamp, data, charts, tables, metadata, citations } = message;
  const isUser = type === MESSAGE_TYPES.USER || type === 'USER';
  const isAgent = type === MESSAGE_TYPES.AGENT || type === 'AGENT';
  const [copied, setCopied] = useState(false);
  const [feedbackState, setFeedbackState] = useState(message.feedback || null); // 'positive' or 'negative'
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [modalImage, setModalImage] = useState({ isOpen: false, src: '', alt: '' });
  const inlineTableIndexRef = useRef(0);
  const inlineTableBlocksRef = useRef([]);
  
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  const messageType = isUser ? 'user' : 'agent';

  const handleCopyMessage = async () => {
    try {
      // Try modern Clipboard API first (requires HTTPS)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(content);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        // Fallback for HTTP or older browsers
        const textArea = document.createElement('textarea');
        textArea.value = content;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (err) {
          console.error('Fallback copy failed:', err);
        }
        
        document.body.removeChild(textArea);
      }
    } catch (error) {
      console.error('Failed to copy message:', error);
    }
  };

  const handleThumbsUp = () => {
    if (feedbackState === 'positive') return; // Already submitted
    setFeedbackState('positive');
    setShowFeedbackForm(false);
    if (onFeedback) {
      onFeedback(message.id, 'positive', null);
    }
  };

  const handleThumbsDown = () => {
    if (feedbackState === 'negative') return; // Already submitted
    setShowFeedbackForm(true);
  };

  const handleSubmitFeedback = async () => {
    setIsSubmittingFeedback(true);
    try {
      if (onFeedback) {
        await onFeedback(message.id, 'negative', feedbackText.trim() || null);
      }
      setFeedbackState('negative');
      setShowFeedbackForm(false);
      setFeedbackText('');
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const handleCancelFeedback = async () => {
    // Submit feedback even when closing without text
    setIsSubmittingFeedback(true);
    try {
      if (onFeedback) {
        await onFeedback(message.id, 'negative', feedbackText.trim() || null);
      }
      setFeedbackState('negative');
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmittingFeedback(false);
      setShowFeedbackForm(false);
      setFeedbackText('');
    }
  };

  const handleRegenerate = () => {
    // ✅ Block if loading or not the last agent message
    if (!onRegenerate || isLoading || !isLastAgentMessage) return;
    onRegenerate(message.id);
  };

  // ✅ Determine if regenerate should be shown/enabled
  const canRegenerate = isLastAgentMessage && !isLoading;

  const ChartRenderer = ({ chart, index, messageCitations }) => {
  const effectiveCitations = messageCitations || citations || [];
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const chartId = `chart-${Date.now()}-${index}`;

  const chartBgColor = isLightMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(14, 37, 72, 0.6)';
  const textColor = isLightMode ? '#333333' : 'rgba(255, 255, 255, 0.85)';
  const gridColor = isLightMode ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)';

  // Parses the chart config from any known backend shape:
  // Shape A (history API):   { config: {...}, title: "...", ... }          → config is a direct object on the chart
  // Shape B (tool output):   { data: '{"config":{...},"title":"..."}', ... } → data is a JSON string
  // Shape C (tool output v2):{ data: { config: {...} }, ... }               → data is a parsed object with config
const loadChartJs = async () => {
    // Load Chart.js if not already loaded
    if (!window.Chart) {
        await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js';
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
        });
    }

    // Load plugins for extended chart types
    const plugins = [
        {
            check: () => window.Chart?.registry?.controllers?.get('matrix'),
            src: 'https://cdn.jsdelivr.net/npm/chartjs-chart-matrix@2.0.1/dist/chartjs-chart-matrix.min.js',
        },
        {
            check: () => window.Chart?.registry?.controllers?.get('treemap'),
            src: 'https://cdn.jsdelivr.net/npm/chartjs-chart-treemap@2.3.1/dist/chartjs-chart-treemap.min.js',
        },
        {
            check: () => window.Chart?.registry?.controllers?.get('sankey'),
            src: 'https://cdn.jsdelivr.net/npm/chartjs-chart-sankey@0.12.1/dist/chartjs-chart-sankey.min.js',
        },
    ];

    await Promise.all(
        plugins.map(({ check, src }) => {
            if (check()) return Promise.resolve();
            return new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = src;
                script.onload = resolve;
                script.onerror = (e) => {
                    console.warn(`Failed to load plugin: ${src}`, e);
                    resolve(); // Don't block other charts if one plugin fails
                };
                document.head.appendChild(script);
            });
        })
    );
};


  const renderConfigChart = async (parsedConfig) => {
    await loadChartJs();

    const { config } = parsedConfig;
    if (!config || !canvasRef.current) return;

    // Destroy any existing instance
    if (chartInstanceRef.current) {
      try { chartInstanceRef.current.destroy(); } catch (e) { /* ignore */ }
      chartInstanceRef.current = null;
    }

    // Apply theme overrides to options
    const themedOptions = {
      ...config.options,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        ...config.options?.plugins,
        legend: {
          ...config.options?.plugins?.legend,
          labels: {
            ...config.options?.plugins?.legend?.labels,
            color: textColor,
          },
        },
      },
      scales: config.options?.scales
        ? Object.fromEntries(
            Object.entries(config.options.scales).map(([key, scale]) => [
              key,
              {
                ...scale,
                ticks: { ...scale.ticks, color: textColor },
                grid: { ...scale.grid, color: gridColor },
              },
            ])
          )
        : undefined,
    };

    chartInstanceRef.current = new window.Chart(canvasRef.current, {
      type: config.type,
      data: config.data,
      options: themedOptions,
    });
  };

  const renderHtmlChart = async (htmlString) => {
    await loadChartJs();

    // Load date-fns adapter if needed
    if (htmlString.includes('time') && !window._chartJsDateAdapter) {
      try {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/npm/chartjs-adapter-date-fns@3.0.0/dist/chartjs-adapter-date-fns.bundle.min.js';
          script.onload = () => { window._chartJsDateAdapter = true; resolve(); };
          script.onerror = reject;
          document.head.appendChild(script);
        });
      } catch (err) {
        console.warn('Date adapter failed to load:', err);
      }
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    let bodyContent = doc.body.innerHTML;

    if (!isLightMode) {
      const replaceBg = (str) => str
        .replace(/background:\s*#fff[^;]*/gi, `background: ${chartBgColor}`)
        .replace(/background:\s*#ffffff[^;]*/gi, `background: ${chartBgColor}`)
        .replace(/background:\s*white[^;]*/gi, `background: ${chartBgColor}`)
        .replace(/background-color:\s*#fff[^;]*/gi, `background-color: ${chartBgColor}`)
        .replace(/background-color:\s*#ffffff[^;]*/gi, `background-color: ${chartBgColor}`)
        .replace(/background-color:\s*white[^;]*/gi, `background-color: ${chartBgColor}`)
        .replace(/background:\s*#f5f5f5[^;]*/gi, `background: ${chartBgColor}`)
        .replace(/background-color:\s*#f5f5f5[^;]*/gi, `background-color: ${chartBgColor}`);
      bodyContent = replaceBg(bodyContent);
    }

    const scripts = Array.from(doc.querySelectorAll('script')).map(s => s.textContent);
    let stylesContent = Array.from(doc.head.querySelectorAll('style')).map(s => s.textContent).join('\n');
    if (!isLightMode) {
      stylesContent = stylesContent
        .replace(/background:\s*#fff[^;]*/gi, `background: ${chartBgColor}`)
        .replace(/background:\s*#ffffff[^;]*/gi, `background: ${chartBgColor}`)
        .replace(/background:\s*white[^;]*/gi, `background: ${chartBgColor}`)
        .replace(/background-color:\s*#fff[^;]*/gi, `background-color: ${chartBgColor}`)
        .replace(/background-color:\s*#ffffff[^;]*/gi, `background-color: ${chartBgColor}`)
        .replace(/background-color:\s*white[^;]*/gi, `background-color: ${chartBgColor}`);
    }

    const originalChartId = doc.querySelector('canvas')?.id || 'generated-chart';
    const modifiedBody = bodyContent.replace(new RegExp(originalChartId, 'g'), chartId);
    const modifiedScripts = scripts.map(s => s.replace(new RegExp(originalChartId, 'g'), chartId));

    if (stylesContent) {
      const styleEl = document.createElement('style');
      styleEl.textContent = stylesContent;
      document.head.appendChild(styleEl);
    }

    containerRef.current.innerHTML = modifiedBody;
    await new Promise(resolve => setTimeout(resolve, 150));

    if (chartInstanceRef.current) {
      try { chartInstanceRef.current.destroy(); } catch (e) { /* ignore */ }
    }

    modifiedScripts.forEach(scriptContent => {
      try {
        const result = eval(scriptContent);
        if (result && typeof result.destroy === 'function') {
          chartInstanceRef.current = result;
        }
      } catch (error) {
        console.error('Script execution failed:', error);
      }
    });

    if (!chartInstanceRef.current) {
      const canvas = containerRef.current.querySelector('canvas');
      if (canvas) chartInstanceRef.current = window.Chart?.getChart?.(canvas);
    }
  };

  useEffect(() => {
    const render = async () => {
      try {
        const parsed = parseChartConfig(chart);

        if (parsed?.config) {
          // New config-based format
          await renderConfigChart(parsed);
        } else if (chart.html) {
          // Legacy HTML string format
          await renderHtmlChart(chart.html);
        }
      } catch (error) {
        console.error('Chart rendering failed:', error);
        if (containerRef.current) {
          containerRef.current.innerHTML = `
            <div style="padding: 20px; text-align: center; color: rgba(255, 255, 255, 0.7);">
              <div style="font-size: 48px;">📊</div>
              <div>Chart failed to load</div>
              <div style="margin-top: 10px; font-size: 12px; color: rgba(255, 255, 255, 0.5);">
                ${error.message || 'Unknown error'}
              </div>
            </div>
          `;
        }
      }
    };

    render();

    return () => {
      if (chartInstanceRef.current) {
        try { chartInstanceRef.current.destroy(); chartInstanceRef.current = null; } catch (e) { /* ignore */ }
      }
      if (canvasRef.current && window.Chart) {
        const existing = window.Chart.getChart(canvasRef.current);
        if (existing) { try { existing.destroy(); } catch (e) { /* ignore */ } }
      }
    };
  }, [chart, chartBgColor, isLightMode]);

  const parsedForTitle = parseChartConfig(chart);
  const chartTitle = parsedForTitle?.title || chart.title || null;
  const isConfigBased = !!(parsedForTitle?.config);
  const canExportChart = !!chartToCsv(chart);

  return (
    <div
      ref={containerRef}
      className={`chart-container-wrapper ${isLightMode ? 'light-mode-chart' : ''}`}
      style={{
        width: '100%',
        background: chartBgColor,
        borderRadius: '8px',
        overflow: 'hidden',
        padding: isConfigBased ? '16px' : '0',
      }}
    >
      {chartTitle && (
        <div style={{
          fontSize: '13px',
          fontWeight: 600,
          color: textColor,
          marginBottom: chart.citations && chart.citations.length > 0 ? '4px' : '12px',
          textAlign: 'center',
          opacity: 0.9,
        }}>
          {chartTitle}
        </div>
      )}
      {isConfigBased && (
        <div style={{ position: 'relative', height: '380px', width: '100%' }}>
          <canvas ref={canvasRef} />
        </div>
      )}
      {chart.citations && chart.citations.length > 0 && effectiveCitations.length > 0 && (() => {
        const chartCits = chart.citations
          .map(ref => effectiveCitations.find(c => c.cite_ref === ref))
          .filter(Boolean)
          .sort((a, b) => a.ref - b.ref);
        if (!chartCits.length) return null;
        return (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '4px',
            marginTop: '8px',
            marginBottom: '4px',
          }}>
            <span style={{
              fontSize: '10px',
              opacity: 0.5,
              color: isLightMode ? '#555' : '#aaa',
              fontStyle: 'italic',
              letterSpacing: '0.2px',
            }}>Source:</span>
            {chartCits.map(cit => (
              <CitationMarker key={cit.cite_ref} num={cit.ref} citation={cit} isLightMode={isLightMode} />
            ))}
          </div>
        );
      })()}
      <div className={styles.chartExportRow}>
        <SaveToCsvButton
          onExport={() => exportChartToCsv(chart, chartTitle || `Chat Chart ${index + 1}`)}
          disabled={!canExportChart}
          isLightMode={isLightMode}
        />
      </div>
    </div>
  );
};


  // Enhanced table validation and fixing
  const validateAndFixMarkdownTable = (content) => {
    if (!content || !content.includes('|')) return content;
    
    try {
      const lines = content.split('\n');
      const tableLines = [];
      let inTable = false;
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        
        if (line.includes('|') && !inTable) {
          inTable = true;
          const headerCols = line.split('|').length - 2;
          
          let j = i;
          while (j < lines.length && lines[j].includes('|')) {
            let tableLine = lines[j].trim();
            
            tableLine = tableLine
              .replace(/\$0\.00/g, '$0.00')
              .replace(/\$(\d+\.\d+)/g, '\\$$$1');
            
            const cols = tableLine.split('|');
            const actualCols = cols.length - 2;
            
            if (actualCols < headerCols) {
              const missing = headerCols - actualCols;
              tableLine = tableLine.slice(0, -1) + '|'.repeat(missing) + '|';
            } else if (actualCols > headerCols) {
              const parts = tableLine.split('|');
              tableLine = parts.slice(0, headerCols + 1).join('|') + '|';
            }
            
            tableLines.push(tableLine);
            j++;
          }
          
          i = j - 1;
          inTable = false;
        } else if (!inTable) {
          tableLines.push(line);
        }
      }
      
      return tableLines.join('\n');
    } catch (error) {
      console.warn('Table validation failed:', error);
      return content;
    }
  };

  // Safe markdown rendering with fallback
  const renderMarkdownContent = (content, options = {}) => {
    const { showTableExport = true } = options;
    if (!content) return null;

    const processCiteChildren = (children) => {
      const processStr = (str) => {
        if (!str.includes(CITE_TOKEN)) return str;
        const parts = str.split(new RegExp(`${CITE_TOKEN}(\\d+)${CITE_TOKEN}`));
        return parts.map((part, i) => {
          if (i % 2 === 1) {
            const ref = parseInt(part, 10);
            const cit = citations?.find(c => c.ref === ref);
            return cit ? <CitationMarker key={i} num={ref} citation={cit} isLightMode={isLightMode} /> : null;
          }
          return part || null;
        }).filter(Boolean);
      };
      if (typeof children === 'string') return processStr(children);
      if (Array.isArray(children)) return children.flatMap(child => typeof child === 'string' ? processStr(child) : child);
      return children;
    };

    try {
      const fixedContent = validateAndFixMarkdownTable(content);
      if (showTableExport) {
        inlineTableIndexRef.current = 0;
        inlineTableBlocksRef.current = extractTablesFromContent(fixedContent);
      }
      
      // 🆕 Convert <imageurl> tags to markdown images
      // Handle images inside tables (no newlines) vs outside tables (with newlines)
      const contentWithImages = fixedContent.replace(
        /<imageurl>\s*(https?:\/\/[^\s<]+)\s*<\/imageurl>/gi,
        (match, url, offset, fullString) => {
          const trimmedUrl = url.trim();
          // Check if we're inside a table row (line contains |)
          const lineStart = fullString.lastIndexOf('\n', offset) + 1;
          const lineEnd = fullString.indexOf('\n', offset);
          const currentLine = fullString.substring(lineStart, lineEnd === -1 ? fullString.length : lineEnd);
          const isInTable = currentLine.includes('|');
          
          if (isInTable) {
            // Inside table: inline image without newlines
            return `![Product](${trimmedUrl})`;
          } else {
            // Outside table: image with spacing
            return `\n\n![Product Image](${trimmedUrl})\n\n`;
          }
        }
      );
      
      // Replace {{cite:N}} with internal token before markdown renders it as plain text
      const contentWithCites = contentWithImages.replace(/\{\{cite:(\d+)\}\}/g, `${CITE_TOKEN}$1${CITE_TOKEN}`);

      return (
        <ReactMarkdown
          className={styles.markdown}
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({children}) => <h1 className={styles.mdH1}>{processCiteChildren(children)}</h1>,
            h2: ({children}) => <h2 className={styles.mdH2}>{processCiteChildren(children)}</h2>,
            h3: ({children}) => <h3 className={styles.mdH3}>{processCiteChildren(children)}</h3>,
            h4: ({children}) => <h4 className={styles.mdH4}>{processCiteChildren(children)}</h4>,
            p: ({children}) => <p className={styles.mdP}>{processCiteChildren(children)}</p>,
            ul: ({children}) => <ul className={styles.mdUl}>{children}</ul>,
            ol: ({children}) => <ol className={styles.mdOl}>{children}</ol>,
            li: ({children}) => <li className={styles.mdLi}>{processCiteChildren(children)}</li>,
            strong: ({children}) => <strong className={styles.mdStrong}>{processCiteChildren(children)}</strong>,
            em: ({children}) => <em className={styles.mdEm}>{processCiteChildren(children)}</em>,
            code: ({children}) => <code className={styles.mdCode}>{children}</code>,
            blockquote: ({children}) => <blockquote className={styles.mdBlockquote}>{children}</blockquote>,
            
            // 🆕 IMAGE HANDLER - handles both table and non-table contexts
            img: ({node, alt, src, ...props}) => {
              // Check if parent is a table cell by looking at the node's position
              // For table images, render inline without wrapper div
              const isTableImage = alt === 'Product'; // Table images use short alt text
              
              const handleImageClick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                setModalImage({ isOpen: true, src, alt: alt || 'Product' });
              };
              
              if (isTableImage) {
                return (
                  <span className={styles.tableImageWrapper} onClick={handleImageClick}>
                    <img 
                      src={src} 
                      alt={alt || 'Product'} 
                      className={styles.tableProductImage}
                      loading="lazy"
                      onError={(e) => {
                        e.target.parentElement.style.display = 'none';
                      }}
                      {...props}
                    />
                    <span className={styles.tableImageZoom}>
                      <ZoomIn size={12} />
                    </span>
                  </span>
                );
              }
              
              return (
                <div className={styles.productImageContainer} onClick={handleImageClick}>
                  <img 
                    src={src} 
                    alt={alt || 'Product'} 
                    className={styles.productImage}
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                    {...props}
                  />
                </div>
              );
            },
            
            thead: ({children}) => <thead className={styles.mdThead}>{children}</thead>,
            tbody: ({children}) => <tbody className={styles.mdTbody}>{children}</tbody>,
            table: ({children}) => {
              const referencedCits = (citations || []).filter(c => c.referenced);
              const tableIndex = showTableExport ? inlineTableIndexRef.current++ : -1;
              const tableBlock = showTableExport ? inlineTableBlocksRef.current[tableIndex] : null;
              const tableFileName = `Chat Table ${tableIndex + 1}`;

              const handleExportTable = () => {
                if (tableBlock) {
                  exportParsedTableToCsv(tableBlock.headers, tableBlock.rows, tableFileName);
                }
              };

              return (
                <div className={styles.mdTableWrapper}>
                  <table className={styles.mdTable}>{children}</table>
                  {showTableExport && (
                    <div className={styles.tableExportRow}>
                      <SaveToCsvButton
                        onExport={handleExportTable}
                        isLightMode={isLightMode}
                      />
                    </div>
                  )}
                  {referencedCits.length > 0 && (
                    <div className={styles.mdTableSource}>
                      <span className={styles.mdTableSourceLabel}>Source:</span>
                      {referencedCits.map(cit => (
                        <CitationMarker key={cit.cite_ref} num={cit.ref} citation={cit} isLightMode={isLightMode} />
                      ))}
                    </div>
                  )}
                </div>
              );
            },
            tr: ({children}) => <tr className={styles.mdTr}>{children}</tr>,
            th: ({children}) => <th className={styles.mdTh}>{processCiteChildren(children)}</th>,
            td: ({children}) => <td className={styles.mdTd}>{processCiteChildren(children)}</td>,
          }}
        >
          {contentWithCites}
        </ReactMarkdown>
      );
    } catch (error) {
      console.error('Markdown rendering failed:', error);
      return (
        <div className={styles.markdownFallback}>
          <pre>{content}</pre>
        </div>
      );
    }
  };

  return (
    <div className={`${styles.message} ${styles[messageType]} ${isLightMode ? styles.lightMode : ''}`}> 
      <div className={styles.messageContent}>
        {isAgent && (
          <div className={styles.avatar}>
            <Bot size={16} strokeWidth={2.5} />
          </div>
        )}
        
        <div className={styles.messageBody}>
          {/* Main response text */}
          {content && (
            <div className={styles.messageText}>
              {renderMarkdownContent(content)}
              {/* Copy button - only show for agent messages */}
              {isAgent && (
                <button 
                  className={styles.copyButton}
                  onClick={handleCopyMessage}
                  title={copied ? "Copied!" : "Copy message"}
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </button>
              )}
            </div>
          )}

          {/* Render tables with proper validation */}
          {tables && Array.isArray(tables) && tables.length > 0 && (
            <div className={styles.tablesContainer}>
              {tables.map((table, index) => (
                <div key={index} className={styles.tableWrapper}>
                  {table?.title && (
                    <h4 className={styles.tableTitle}>{table.title}</h4>
                  )}
                  <div className={styles.tableContent}>
                    {table?.markdown ? (
                      renderMarkdownContent(table.markdown, { showTableExport: false })
                    ) : table?.html ? (
                      <div dangerouslySetInnerHTML={{ __html: table.html }} />
                    ) : (
                      <div className={styles.tableText}>No table data</div>
                    )}
                  </div>
                  <div className={styles.tableExportRow}>
                    <SaveToCsvButton
                      isLightMode={isLightMode}
                      onExport={() => {
                        const fileName = table?.title || `Chat Table ${index + 1}`;
                        if (table?.markdown) {
                          exportMarkdownTableToCsv(table.markdown, fileName);
                        } else if (table?.html) {
                          exportHtmlTableToCsv(table.html, fileName);
                        }
                      }}
                      disabled={!table?.markdown && !table?.html}
                    />
                  </div>
                  {citations && citations.length > 0 && (() => {
                    const referencedCits = citations.filter(c => c.referenced);
                    if (!referencedCits.length) return null;
                    return (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        marginTop: '6px',
                        marginBottom: '2px',
                        paddingLeft: '2px',
                      }}>
                        <span style={{
                          fontSize: '10px',
                          opacity: 0.5,
                          color: isLightMode ? '#555' : '#aaa',
                          fontStyle: 'italic',
                          letterSpacing: '0.2px',
                        }}>Source:</span>
                        {referencedCits.map(cit => (
                          <CitationMarker key={cit.cite_ref} num={cit.ref} citation={cit} isLightMode={isLightMode} />
                        ))}
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
          
          {/* Render charts with brutal script execution */}
          {charts && charts.length > 0 && (
            <div className={styles.chartsContainer}>
              {charts.map((chart, index) => (
                <div key={index} className={styles.chartWrapper}>
                  <ChartRenderer chart={chart} index={index} messageCitations={citations} />
                </div>
              ))}
            </div>
          )}

          {/* Feedback and Regenerate buttons - only show for agent messages, AFTER charts/tables */}
          {isAgent && (
            <div className={styles.messageActions}>
              <div className={styles.feedbackContainer}>
                <button
                  className={`${styles.feedbackButton} ${feedbackState === 'positive' ? styles.feedbackActive : ''}`}
                  onClick={handleThumbsUp}
                  disabled={feedbackState !== null}
                  title="Good response"
                >
                  <ThumbsUp size={14} />
                </button>
                <button
                  className={`${styles.feedbackButton} ${feedbackState === 'negative' ? styles.feedbackActive : ''}`}
                  onClick={handleThumbsDown}
                  disabled={feedbackState !== null}
                  title="Poor response"
                >
                  <ThumbsDown size={14} />
                </button>
                
                {/* ✅ UPDATED: Only show regenerate on last agent message, disable during loading */}
                {canRegenerate && (
                  <button
                    className={`${styles.regenerateButton} ${isLoading ? styles.regenerateDisabled : ''}`}
                    onClick={handleRegenerate}
                    disabled={isLoading}
                    title={isLoading ? "Please wait..." : "Regenerate response"}
                  >
                    <RefreshCw size={14} className={isLoading ? styles.spinning : ''} />
                    <span>{isLoading ? 'Processing...' : 'Regenerate'}</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Feedback form - shows when thumbs down is clicked */}
          {showFeedbackForm && (
            <div className={styles.feedbackForm}>
              <div className={styles.feedbackFormHeader}>
                <h4>What went wrong?</h4>
                <button
                  className={styles.feedbackFormClose}
                  onClick={handleCancelFeedback}
                  title="Cancel"
                >
                  <X size={16} />
                </button>
              </div>
              <textarea
                className={styles.feedbackTextarea}
                placeholder="Please tell us what went wrong or how we can improve..."
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                rows={4}
                autoFocus
              />
              <div className={styles.feedbackFormActions}>
                <button
                  className={styles.feedbackCancelButton}
                  onClick={handleCancelFeedback}
                  disabled={isSubmittingFeedback}
                >
                  Cancel
                </button>
                <button
                  className={styles.feedbackSubmitButton}
                  onClick={handleSubmitFeedback}
                  disabled={isSubmittingFeedback}
                >
                  {isSubmittingFeedback ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </div>
          )}
          
          <div className={styles.timestamp}>
            {formatTime(timestamp)}
          </div>
        </div>
        
        {isUser && (
          <div className={styles.avatar}>
            <User size={16} strokeWidth={2.5} />
          </div>
        )}
      </div>
      
      {/* Image Modal for expanding images */}
      <ImageModal 
        src={modalImage.src}
        alt={modalImage.alt}
        isOpen={modalImage.isOpen}
        onClose={() => setModalImage({ isOpen: false, src: '', alt: '' })}
        isLightMode={isLightMode}
      />
    </div>
  );
}, (prevProps, nextProps) => {
  // ✅ UPDATE: Add isLoading and isLastAgentMessage to comparison
  return prevProps.message.id === nextProps.message.id &&
         prevProps.message.content === nextProps.message.content &&
         prevProps.message.charts === nextProps.message.charts &&
         prevProps.message.tables === nextProps.message.tables &&
         prevProps.message.feedback === nextProps.message.feedback &&
         prevProps.message.citations === nextProps.message.citations &&
         prevProps.isLightMode === nextProps.isLightMode &&
         prevProps.isLoading === nextProps.isLoading &&
         prevProps.isLastAgentMessage === nextProps.isLastAgentMessage;
});

ChatMessage.displayName = 'ChatMessage';

export default ChatMessage;
